import { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from './lib/supabase';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const WS_URL = API.replace('http', 'ws');
const MAX_GUEST_GAMES = 10;
const GUEST_SESSION_KEY = 'pw_guest_session';

function cleanMarkdown(text) {
  return text
    .replace(/#{1,6}\s*/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_{1,2}(.*?)_{1,2}/g, '$1')
    .replace(/`{1,3}[^`]*`{1,3}/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^.*?PROMPT WARS.*?\n?/i, '')  // strip header line
    .replace(/[-—]{3,}\s*$/g, '')           // strip trailing ---
    .trim();
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,400&family=Share+Tech+Mono&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #080508; --bg2: #100d12; --surface: #160f1a; --border: #2e1a3a;
    --gold: #a8893c; --gold2: #c9a84c; --purple: #7b3fa0; --purple2: #a855d4;
    --crimson: #6b1420; --crimson2: #9b1e2e;
    --text: #e8ddd0; --muted: #7a6a70; --glow: rgba(123,63,160,0.12);
  }
  html, body, #root { height: 100%; }
  body {
    background: var(--bg);
    color: var(--text);
    font-family: 'Crimson Pro', serif;
    font-size: 18px;
    overflow-x: hidden;
  }
  body::before {
    content: '';
    position: fixed; inset: 0;
    background-image:
      linear-gradient(rgba(123,63,160,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(123,63,160,0.04) 1px, transparent 1px);
    background-size: 50px 50px;
    pointer-events: none; z-index: 0;
  }
  .app { position: relative; z-index: 1; min-height: 100vh; display: flex; flex-direction: column; }
  .nav {
    display: flex; align-items: center; justify-content: space-between;
    padding: 1rem 2rem; border-bottom: 1px solid var(--border);
    background: rgba(8,5,8,0.8); backdrop-filter: blur(8px);
    position: sticky; top: 0; z-index: 100;
  }
  .nav-logo {
    font-family: 'Cinzel Decorative', serif; font-size: 1.1rem; font-weight: 900;
    color: var(--gold);
    text-decoration: none;
  }
  .nav-links { display: flex; gap: 0.5rem; align-items: center; }
  .nav-btn {
    background: none; border: 1px solid #3a2a45; color: var(--muted);
    padding: 0.35rem 1rem; font-family: 'Share Tech Mono', monospace;
    font-size: 0.72rem; letter-spacing: 0.1em; cursor: pointer;
    transition: all 0.2s; text-transform: uppercase; text-decoration: none;
    display: inline-block;
  }
  .nav-btn:hover, .nav-btn.active { border-color: var(--gold); color: var(--gold); background: var(--glow); }
  .main { flex: 1; padding: 2rem 1rem; max-width: 900px; margin: 0 auto; width: 100%; }
  .card { background: var(--surface); border: 1px solid var(--border); padding: 2rem; position: relative; }
  .card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--purple), transparent); }
  .card.gold::before { background: linear-gradient(90deg, transparent, var(--gold), transparent); }
  .input {
    width: 100%; background: var(--bg2); border: 1px solid var(--border);
    color: var(--text); padding: 0.75rem 1rem; font-family: 'Crimson Pro', serif;
    font-size: 1.1rem; outline: none; transition: border-color 0.2s;
  }
  .input:focus { border-color: var(--purple2); }
  .input::placeholder { color: var(--muted); }
  .btn {
    background: var(--crimson2);
    border: none; color: var(--text); padding: 0.75rem 2rem;
    font-family: 'Cinzel Decorative', serif; font-size: 0.85rem;
    cursor: pointer; letter-spacing: 0.05em; transition: all 0.2s;
  }
  .btn:hover { filter: brightness(1.2); transform: translateY(-1px); }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
  .btn.purple { background: var(--purple); }
  .btn.gold { background: var(--gold); color: var(--bg); }
  .btn.ghost { background: rgba(255,255,255,0.03); border: 1px solid var(--muted); color: var(--text); }
  .btn.ghost:hover { border-color: var(--gold); color: var(--gold); filter: none; transform: translateY(-1px); }
  h1, h2 { font-family: 'Cinzel Decorative', serif; color: var(--gold); letter-spacing: 0.05em; }
  h2 { font-size: 1.1rem; margin-bottom: 1.5rem; }
  .err { font-family: 'Share Tech Mono', monospace; font-size: 0.78rem; color: var(--crimson2); margin-top: 0.5rem; }
  .hero { text-align: center; padding: 3rem 1rem 2rem; }
  .hero-title {
    font-family: 'Cinzel Decorative', serif; font-weight: 900;
    font-size: clamp(2rem, 6vw, 4rem);
    color: var(--gold);
    line-height: 1.1; margin-bottom: 1rem;
  }
  .hero-sub { color: var(--muted); font-size: 1.2rem; max-width: 500px; margin: 0 auto 2.5rem; line-height: 1.6; }
  .hero-actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
  .lives-badge {
    display: inline-flex; align-items: center; gap: 0.4rem;
    font-family: 'Share Tech Mono', monospace; font-size: 0.72rem;
    color: var(--gold); border: 1px solid var(--gold);
    padding: 0.25rem 0.75rem; margin-bottom: 2.5rem;
  }
  .features { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; margin-top: 2rem; }
  .feature { text-align: center; padding: 2rem 1.5rem; border: 1px solid var(--border); background: #0f0d11; }
  .feature-icon { font-size: 2.5rem; margin-bottom: 1rem; }
  .feature-title { font-family: 'Cinzel Decorative', serif; font-size: 0.9rem; color: var(--gold); margin-bottom: 0.5rem; }
  .feature-desc { color: var(--muted); font-size: 0.95rem; line-height: 1.5; }
  .auth-wrap { max-width: 420px; margin: 4rem auto; }
  .auth-form { display: flex; flex-direction: column; gap: 1rem; }
  .auth-switch { font-family: 'Share Tech Mono', monospace; font-size: 0.75rem; color: var(--muted); text-align: center; margin-top: 1rem; }
  .auth-switch a { color: var(--purple2); text-decoration: none; }
  .battle-vs { display: flex; align-items: center; justify-content: space-between; margin-bottom: 2rem; gap: 1rem; }
  .fighter-name { font-family: 'Cinzel Decorative', serif; font-size: clamp(0.7rem, 2vw, 1rem); color: var(--gold); flex: 1; text-align: center; }
  .vs-badge { font-family: 'Cinzel Decorative', serif; font-size: 1.5rem; color: var(--crimson2); text-shadow: 0 0 20px var(--crimson2); }
  .timer-display { text-align: center; margin-bottom: 1.5rem; }
  .timer { font-family: 'Share Tech Mono', monospace; font-size: 3rem; color: var(--gold); }
  .timer.urgent { color: var(--crimson2); animation: flicker 0.5s infinite; }
  @keyframes flicker { 0%,100%{opacity:1} 50%{opacity:0.6} }
  .prompt-label { font-family: 'Share Tech Mono', monospace; font-size: 0.68rem; color: var(--muted); letter-spacing: 0.2em; text-transform: uppercase; margin-bottom: 0.5rem; }
  .prompt-textarea {
    width: 100%; background: var(--bg2); border: 1px solid var(--border);
    color: var(--text); padding: 1rem; font-family: 'Crimson Pro', serif;
    font-size: 1.1rem; resize: none; height: 110px; outline: none; transition: border-color 0.2s;
  }
  .prompt-textarea:focus { border-color: var(--purple2); }
  .prompt-textarea:disabled { opacity: 0.5; }
  .char-count { font-family: 'Share Tech Mono', monospace; font-size: 0.68rem; color: var(--muted); text-align: right; margin-top: 0.25rem; }
  .ready-tag { font-family: 'Share Tech Mono', monospace; font-size: 0.72rem; color: #4caf50; letter-spacing: 0.1em; padding: 0.5rem 1rem; border: 1px solid #4caf50; display: inline-block; margin-top: 1rem; }
  .opp-ready { font-family: 'Share Tech Mono', monospace; font-size: 0.72rem; color: var(--purple2); text-align: center; margin-top: 1rem; animation: fadeIn 0.4s; }
  @keyframes fadeIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
  .fighter-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.5rem; }
  .fighter-card { border: 1px solid var(--border); padding: 1rem 1.25rem; background: var(--bg2); position: relative; }
  .fighter-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, var(--gold), transparent); }
  .fighter-card-name { font-family: 'Cinzel Decorative', serif; font-size: 0.72rem; color: var(--gold); letter-spacing: 0.05em; margin-bottom: 0.5rem; }
  .fighter-card-prompt { font-style: italic; color: var(--text); font-size: 0.95rem; line-height: 1.6; }
  .narration-text { font-style: normal; line-height: 1.7; color: #e8dcc8; font-size: 1rem; min-height: 80px; border-left: 2px solid var(--gold); padding: 1.25rem 1.5rem; margin: 0; }
  .narration-wrap { background: var(--bg); padding: 2rem; }
  .match-banner { display: flex; align-items: center; justify-content: center; gap: 1.5rem; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--border); margin-bottom: 2rem; }
  .match-banner-name { font-family: 'Share Tech Mono', monospace; font-size: 0.8rem; color: var(--gold); letter-spacing: 0.1em; }
  .match-banner-vs { font-family: 'Cinzel Decorative', serif; font-size: 1rem; color: var(--crimson2); }
  .result-glow { animation: resultReveal 0.8s ease forwards; }
  @keyframes resultReveal { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  .cursor-blink { display: inline-block; width: 2px; height: 1em; background: var(--gold); margin-left: 2px; vertical-align: text-bottom; animation: blink 1s step-end infinite; }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
  .result-center { text-align: center; padding: 2rem 0; }
  .result-icon { font-size: 3.5rem; margin-bottom: 1rem; }
  .result-title { font-family: 'Cinzel Decorative', serif; font-size: clamp(1.2rem, 3vw, 1.8rem); margin-bottom: 0.5rem; }
  .result-title.win { color: var(--gold2); }
  .result-title.lose { color: var(--crimson2); }
  .elo-delta { font-family: 'Share Tech Mono', monospace; font-size: 1.1rem; margin: 0.5rem 0; }
  .elo-delta.pos { color: #4caf50; }
  .elo-delta.neg { color: var(--crimson2); }
  .result-btns { display: flex; gap: 1rem; justify-content: center; margin-top: 2rem; flex-wrap: wrap; }
  .lives-bar { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem; }
  .life-pip { width: 18px; height: 18px; border-radius: 50%; background: var(--crimson2); transition: background 0.3s; }
  .life-pip.empty { background: var(--border); }
  .lives-label { font-family: 'Share Tech Mono', monospace; font-size: 0.72rem; color: var(--muted); margin-left: 0.5rem; }
  .regen-info { font-family: 'Share Tech Mono', monospace; font-size: 0.68rem; color: var(--muted); margin-top: 0.5rem; }
  .queue-wrap { display: flex; flex-direction: column; align-items: center; gap: 2rem; padding-top: 3rem; text-align: center; }
  .pulse-ring { width: 110px; height: 110px; border-radius: 50%; border: 2px solid var(--purple2); display: flex; align-items: center; justify-content: center; animation: pulse 2s ease-in-out infinite; }
  @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(168,85,212,0.3)} 50%{box-shadow:0 0 0 20px rgba(168,85,212,0)} }
  .no-lives-wrap { text-align: center; padding: 2rem 0; }
  .pack-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; margin-top: 1.5rem; }
  .pack-card { border: 1px solid var(--border); padding: 1.5rem; text-align: center; background: var(--bg2); transition: border-color 0.2s; cursor: pointer; }
  .pack-card:hover { border-color: var(--gold); }
  .pack-lives { font-family: 'Cinzel Decorative', serif; font-size: 1.8rem; color: var(--gold); }
  .pack-name { font-family: 'Share Tech Mono', monospace; font-size: 0.7rem; color: var(--muted); letter-spacing: 0.1em; text-transform: uppercase; margin: 0.25rem 0; }
  .pack-price { font-size: 1.2rem; color: var(--text); }
  .ladder-table { width: 100%; border-collapse: collapse; }
  .ladder-table th { font-family: 'Share Tech Mono', monospace; font-size: 0.68rem; letter-spacing: 0.15em; color: var(--muted); text-align: left; padding: 0.75rem 1rem; border-bottom: 1px solid var(--border); text-transform: uppercase; }
  .ladder-table td { padding: 0.75rem 1rem; border-bottom: 1px solid rgba(46,26,58,0.5); font-size: 1rem; }
  .ladder-table tr:hover td { background: rgba(123,63,160,0.05); }
  .rank-num { font-family: 'Share Tech Mono', monospace; color: var(--muted); font-size: 0.85rem; }
  .elo-val { font-family: 'Share Tech Mono', monospace; color: var(--gold); font-size: 0.9rem; }
  .you-tag { font-family: 'Share Tech Mono', monospace; font-size: 0.58rem; color: var(--bg); background: var(--purple2); padding: 1px 5px; margin-left: 6px; vertical-align: middle; }
  .wl { font-family: 'Share Tech Mono', monospace; font-size: 0.8rem; color: var(--muted); }
  .stat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 2rem; }
  .stat-box { text-align: center; padding: 1.5rem; border: 1px solid var(--border); background: var(--bg2); }
  .stat-val { font-family: 'Cinzel Decorative', serif; font-size: 1.8rem; color: var(--gold); }
  .stat-key { font-family: 'Share Tech Mono', monospace; font-size: 0.65rem; color: var(--muted); letter-spacing: 0.2em; text-transform: uppercase; margin-top: 0.25rem; }
  .guest-banner { background: rgba(123,63,160,0.1); border: 1px solid var(--purple); padding: 1.25rem 1.5rem; margin-bottom: 1.5rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
  .guest-banner-text { font-family: 'Share Tech Mono', monospace; font-size: 0.78rem; color: var(--text); }
  .guest-banner-text span { color: var(--gold); }
  .mono { font-family: 'Share Tech Mono', monospace; }
  .muted { color: var(--muted); }
  .sprite-arena { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 1.5rem; padding: 0 2rem; }
  .sprite-fighter { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
  .sprite-fighter.flipped img { transform: scaleX(-1); }
  .sprite-img { width: 96px; height: 96px; image-rendering: pixelated; }
  .sprite-placeholder { width: 96px; height: 96px; border: 1px dashed var(--border); display: flex; align-items: center; justify-content: center; color: var(--muted); font-size: 1.5rem; }
  .sprite-name { font-family: 'Share Tech Mono', monospace; font-size: 0.68rem; color: var(--gold); letter-spacing: 0.1em; }
  /* Footer */
  .footer {
    border-top: 1px solid var(--border);
    padding: 2rem;
    margin-top: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  .footer-links { display: flex; gap: 2rem; align-items: center; flex-wrap: wrap; justify-content: center; }
  .footer-link {
    font-family: 'Share Tech Mono', monospace; font-size: 0.68rem;
    color: var(--muted); text-decoration: none; letter-spacing: 0.1em;
    text-transform: uppercase; transition: color 0.2s;
  }
  .footer-link:hover { color: var(--gold); }
  .footer-copy { font-family: 'Share Tech Mono', monospace; font-size: 0.65rem; color: var(--muted); letter-spacing: 0.1em; }
  .footer-dot { color: var(--border); }
`;

const PHASE = { MENU:'menu', QUEUE:'queue', PROMPT:'prompt', NARRATION:'narration', RESULT:'result', NO_LIVES:'no_lives' };

export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      fetch(`${API}/api/profile/${user.id}`).then(r => r.json()).then(setProfile).catch(() => {});
    } else {
      setProfile(null);
    }
  }, [user]);

  if (loading) return <></>;

  return (
    <>
      <style>{CSS}</style>
      <div className="app">
        <NavBar user={user} profile={profile} />
        <Routes>
          <Route path="/" element={<Landing user={user} />} />
          <Route path="/register" element={<Register setUser={setUser} />} />
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/play" element={<Play user={user} profile={profile} setProfile={setProfile} />} />
          <Route path="/ladder" element={<Ladder profile={profile} />} />
          <Route path="/profile" element={<Profile user={user} profile={profile} setProfile={setProfile} />} />
        </Routes>
        <Footer />
      </div>
    </>
  );
}

function NavBar({ user, profile }) {
  const navigate = useNavigate();
  const logout = async () => { await supabase.auth.signOut(); navigate('/'); };
  return (
    <nav className="nav">
      <Link to="/" className="nav-logo">Prompt Wars</Link>
      <div className="nav-links">
        <Link to="/play" className="nav-btn">Play</Link>
        <Link to="/ladder" className="nav-btn">Ladder</Link>
        {user ? (
          <>
            <Link to="/profile" className="nav-btn">{profile?.game_tag || 'Profile'}</Link>
            <button className="nav-btn" onClick={logout}>Logout</button>
          </>
        ) : (
          <>
            <Link to="/login" className="nav-btn">Login</Link>
            <Link to="/register" className="nav-btn">Register</Link>
          </>
        )}
      </div>
    </nav>
  );
}

function Landing({ user }) {
  const navigate = useNavigate();
  return (
    <main className="main">
      <div className="hero">
        <div className="lives-badge">⚔ 10 FREE GAMES · NO ACCOUNT NEEDED</div>
        <h1 className="hero-title">Describe Your Fighter.<br />Conquer the Arena.</h1>
        <p className="hero-sub">Write one sentence. An AI narrator decides your fate. Climb the global ladder.</p>
        <div className="hero-actions">
          <button className="btn" style={{fontSize:'1rem', padding:'0.9rem 2.5rem'}} onClick={() => navigate('/play')}>⚔ Play Now — Free</button>
          {!user && <button className="btn ghost" style={{fontSize:'1rem', padding:'0.9rem 2.5rem'}} onClick={() => navigate('/register')}>Create Account</button>}
        </div>
      </div>
      <div className="features">
        {[
          ['⚔', 'Describe to Fight', 'One sentence summons your champion. The more creative, the stronger.'],
          ['🎭', 'AI Narrator', 'Claude narrates the battle in real-time and picks a winner based on your creativity.'],
          ['🏆', 'Climb the Ladder', 'Win ELO, climb the global leaderboard, and earn your game tag a permanent rank.'],
          ['❤️', 'Lives System', 'Start with 10 lives. Regen 1/hour for free, or buy packs to keep the battles going.'],
        ].map(([icon, title, desc]) => (
          <div className="feature" key={title}>
            <div className="feature-icon">{icon}</div>
            <div className="feature-title">{title}</div>
            <div className="feature-desc">{desc}</div>
          </div>
        ))}
      </div>
    </main>
  );
}

function Register({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    const { data, error: err } = await supabase.auth.signUp({ email, password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setUser(data.user); navigate('/play');
  };
  return (
    <main className="main">
      <div className="auth-wrap">
        <div className="card gold">
          <h2>Create Account</h2>
          <p className="muted" style={{marginBottom:'1.5rem', fontSize:'0.95rem', lineHeight:1.6}}>Your account is permanent. Your ELO, rank, and game tag can never be lost.</p>
          <form className="auth-form" onSubmit={submit}>
            <input className="input" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
            <input className="input" type="password" placeholder="Password (min 6 chars)" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
            {error && <div className="err">{error}</div>}
            <button className="btn gold" type="submit" disabled={loading}>{loading ? 'Creating...' : 'Enter the Arena'}</button>
          </form>
          <div className="auth-switch">Already have an account? <Link to="/login">Login</Link></div>
        </div>
      </div>
    </main>
  );
}

function Login({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setUser(data.user); navigate('/play');
  };
  return (
    <main className="main">
      <div className="auth-wrap">
        <div className="card">
          <h2>Welcome Back</h2>
          <form className="auth-form" onSubmit={submit}>
            <input className="input" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
            <input className="input" type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
            {error && <div className="err">{error}</div>}
            <button className="btn purple" type="submit" disabled={loading}>{loading ? 'Logging in...' : 'Enter'}</button>
          </form>
          <div className="auth-switch">No account? <Link to="/register">Register</Link></div>
        </div>
      </div>
    </main>
  );
}

function SpriteArena({ room, sprites, lockedPrompts }) {
  return (
    <div className="sprite-arena">
      {room?.players?.map((p, i) => (
        <div key={p.id} className={`sprite-fighter ${i === 1 ? 'flipped' : ''}`}>
          {sprites[p.id]
            ? <img className="sprite-img" src={sprites[p.id]} alt={p.username} />
            : <div className="sprite-placeholder">⚔</div>
          }
          <div className="sprite-name">{p.username}</div>
          {lockedPrompts[p.id] && <div className="muted" style={{fontSize:'0.75rem', fontStyle:'italic', maxWidth:'160px', textAlign:'center'}}>{lockedPrompts[p.id]}</div>}
        </div>
      ))}
    </div>
  );
}

function Play({ user, profile, setProfile }) {
  const [phase, setPhase] = useState(PHASE.MENU);
  const [room, setRoom] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [opponentReady, setOpponentReady] = useState(false);
  const [timer, setTimer] = useState(60);
  const [narration, setNarration] = useState('');
  const [isNarrating, setIsNarrating] = useState(false);
  const [result, setResult] = useState(null);
  const [guestGames, setGuestGames] = useState(0);
  const [lockedPrompts, setLockedPrompts] = useState({});
  const [nextRegen, setNextRegen] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const [sprites, setSprites] = useState({});

  useEffect(() => {
    if (!user) {
      const sess = localStorage.getItem(GUEST_SESSION_KEY);
      const data = sess ? JSON.parse(sess) : { games: 0 };
      setGuestGames(data.games);
    }
  }, [user]);

  useEffect(() => {
    if (location.search.includes('purchase=success') && user) {
      fetch(`${API}/api/profile/${user.id}`).then(r => r.json()).then(setProfile).catch(() => {});
    }
  }, [location, user, setProfile]);

  useEffect(() => {
    if (phase === PHASE.NO_LIVES && user) {
      fetch(`${API}/api/profile/${user.id}/regen`)
        .then(r => r.json())
        .then(d => setNextRegen(new Date(d.next_regen_at)))
        .catch(() => {});
    }
  }, [phase, user]);

  const connectWS = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return wsRef.current;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === 'no_lives') { setPhase(PHASE.NO_LIVES); }
      if (msg.type === 'match_found') {
        setRoom(msg); setLockedPrompts({});
        setSprites({});
        setPhase(PHASE.PROMPT); setTimer(60); setPrompt(''); setSubmitted(false); setOpponentReady(false);
        timerRef.current = setInterval(() => setTimer(t => t <= 1 ? (clearInterval(timerRef.current), 0) : t - 1), 1000);
      }
      if (msg.type === 'player_ready' && msg.playerId !== (user?.id || 'guest')) {
        setOpponentReady(true);
        if (msg.prompt) setLockedPrompts(p => ({ ...p, [msg.playerId]: msg.prompt }));
      }
      if (msg.type === 'sprites_ready') {
        setSprites(msg.sprites);
      }
      if (msg.type === 'battle_start') {
        clearInterval(timerRef.current);
        setNarration(''); setIsNarrating(true); setPhase(PHASE.NARRATION);
      }
      if (msg.type === 'narration_chunk') { setNarration(n => n + msg.text); }
      if (msg.type === 'battle_end') {
        setIsNarrating(false); setResult(msg);
        if (!user) {
          const newCount = guestGames + 1;
          setGuestGames(newCount);
          localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify({ games: newCount }));
        } else {
          fetch(`${API}/api/profile/${user.id}`).then(r => r.json()).then(setProfile).catch(() => {});
        }
        setTimeout(() => setPhase(PHASE.RESULT), 600);
      }
      if (msg.type === 'opponent_disconnected') {
        clearInterval(timerRef.current); alert('Opponent disconnected.'); setPhase(PHASE.MENU);
      }
    };
    return ws;
  }, [user, guestGames, setProfile]);

  const joinQueue = useCallback(() => {
    if (!user && guestGames >= MAX_GUEST_GAMES) { navigate('/register'); return; }
    const ws = connectWS();
    const payload = {
      type: 'join_queue',
      playerId: user?.id || `guest_${Date.now()}`,
      username: profile?.game_tag || `Guest${Math.floor(Math.random()*9000+1000)}`,
      isGuest: !user
    };
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload)); setPhase(PHASE.QUEUE);
    } else {
      ws.addEventListener('open', () => { ws.send(JSON.stringify(payload)); setPhase(PHASE.QUEUE); }, { once: true });
    }
  }, [user, profile, guestGames, connectWS, navigate]);

  const submitPrompt = () => {
    if (!prompt.trim() || submitted) return;
    wsRef.current?.send(JSON.stringify({ type: 'submit_prompt', prompt: prompt.trim() }));
    setLockedPrompts(p => ({ ...p, [user?.id || 'guest']: prompt.trim() }));
    setSubmitted(true);
  };

  const buyPack = async (pack) => {
    if (!user) { navigate('/register'); return; }
    const res = await fetch(`${API}/api/stripe/checkout`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pack, userId: user.id })
    });
    const { url } = await res.json();
    window.location.href = url;
  };

  const opponentName = room?.players?.find(p => p.id !== (user?.id || 'guest'))?.username;
  const myName = room?.players?.find(p => p.id === (user?.id || 'guest'))?.username || profile?.game_tag || 'You';
  const displayNarration = cleanMarkdown(narration.replace(/WINNER:.*$/im, '').trim());

  return (
    <main className="main">
      {!user && phase === PHASE.MENU && (
        <div className="guest-banner">
          <div className="guest-banner-text">Playing as guest · <span>{MAX_GUEST_GAMES - guestGames} free games remaining</span></div>
          <Link to="/register"><button className="btn purple" style={{padding:'0.4rem 1.2rem', fontSize:'0.8rem'}}>Save Progress →</button></Link>
        </div>
      )}

      {phase === PHASE.MENU && (
        <div style={{display:'flex', flexDirection:'column', gap:'1.5rem', alignItems:'center', paddingTop:'1.5rem'}}>
          <div className="card gold" style={{maxWidth:'480px', width:'100%', textAlign:'center'}}>
            {profile && (
              <>
                <div className="stat-grid" style={{marginBottom:'1.5rem'}}>
                  {[['ELO', profile.elo], ['Wins', profile.wins], ['Losses', profile.losses]].map(([k,v]) => (
                    <div className="stat-box" key={k}><div className="stat-val">{v}</div><div className="stat-key">{k}</div></div>
                  ))}
                </div>
                <div className="lives-bar" style={{justifyContent:'center'}}>
                  {Array.from({length:10}).map((_,i) => <div key={i} className={`life-pip ${i < profile.lives ? '' : 'empty'}`} />)}
                  <div className="lives-label">{profile.lives}/10 lives</div>
                </div>
              </>
            )}
            <button className="btn" style={{width:'100%', fontSize:'1rem', padding:'1rem', marginTop:'1.5rem'}} onClick={joinQueue}>⚔ Find Battle</button>
            {!user && <div className="muted mono" style={{fontSize:'0.7rem', marginTop:'0.75rem'}}>{MAX_GUEST_GAMES - guestGames} free games left</div>}
          </div>
        </div>
      )}

      {phase === PHASE.QUEUE && (
        <div className="queue-wrap">
          <div className="pulse-ring"><span style={{fontSize:'2.5rem'}}>⚔</span></div>
          <div>
            <div style={{fontFamily:'Cinzel Decorative', fontSize:'1rem', color:'var(--gold)', marginBottom:'0.5rem'}}>Seeking an opponent</div>
            <div className="mono muted" style={{fontSize:'0.8rem'}}>Standing by in the arena...</div>
          </div>
          <button className="btn ghost" onClick={() => { wsRef.current?.send(JSON.stringify({type:'leave_queue'})); setPhase(PHASE.MENU); }}>Leave Queue</button>
        </div>
      )}

      {phase === PHASE.PROMPT && (
        <div>
          <div className="battle-vs">
            <div className="fighter-name">{myName}</div>
            <div className="vs-badge">VS</div>
            <div className="fighter-name">{opponentName}</div>
          </div>
          <div className="timer-display">
            <div className={`timer ${timer <= 10 ? 'urgent' : ''}`}>{String(timer).padStart(2,'0')}s</div>
            <div className="mono muted" style={{fontSize:'0.75rem'}}>to describe your fighter</div>
          </div>
          <div className="card">
            <div className="prompt-label">Describe your fighter</div>
            <textarea
              className="prompt-textarea"
              placeholder="e.g. A retired mime who fights in complete silence, making opponents confused about what's real..."
              value={prompt}
              onChange={e => setPrompt(e.target.value.slice(0, 280))}
              disabled={submitted}
              autoFocus
            />
            <div className="char-count">{prompt.length}/280</div>
            {!submitted
              ? <button className="btn gold" style={{marginTop:'1rem', width:'100%'}} onClick={submitPrompt} disabled={!prompt.trim()}>Lock In Fighter</button>
              : <div className="ready-tag">✓ Fighter locked — awaiting opponent</div>
            }
            {opponentReady && <div className="opp-ready">⚡ {opponentName} has locked in!</div>}
          </div>
        </div>
      )}

      {phase === PHASE.NARRATION && (
        <div>
          <div className="match-banner">
            <div className="match-banner-name">{myName}</div>
            <div className="match-banner-vs">⚔</div>
            <div className="match-banner-name">{opponentName}</div>
          </div>
          <SpriteArena room={room} sprites={sprites} lockedPrompts={lockedPrompts} />
          <div className="narration-wrap">
            <div className="narration-text">
              {displayNarration}
              {isNarrating && <span className="cursor-blink" />}
            </div>
          </div>
        </div>
      )}

      {phase === PHASE.RESULT && result && (
        <div className="result-glow">
          <div className="match-banner">
            <div className="match-banner-name">{myName}</div>
            <div className="match-banner-vs">⚔</div>
            <div className="match-banner-name">{opponentName}</div>
          </div>
          <SpriteArena room={room} sprites={sprites} lockedPrompts={lockedPrompts} />
          <div className="narration-wrap" style={{marginBottom:'1.5rem'}}>
            <div className="narration-text">{displayNarration}</div>
          </div>
          <div className="card gold">
            <div className="result-center">
              <div className="result-icon">{result.winnerId === user?.id ? '👑' : '💀'}</div>
              <div className={`result-title ${result.winnerId === user?.id ? 'win' : 'lose'}`}>
                {result.winnerId === user?.id ? 'Victory!' : `${result.winnerName} wins`}
              </div>
              {result.eloChanges[user?.id] && (
                <div className={`elo-delta ${result.winnerId === user?.id ? 'pos' : 'neg'}`}>
                  {result.eloChanges[user?.id]} ELO
                </div>
              )}
              {!user && <div className="muted mono" style={{fontSize:'0.75rem', marginTop:'0.5rem'}}>{MAX_GUEST_GAMES - guestGames} free games left</div>}
            </div>
            <div className="result-btns">
              <button className="btn" onClick={joinQueue}>Fight Again</button>
              <Link to="/ladder"><button className="btn purple">View Ladder</button></Link>
              <button className="btn ghost" onClick={() => setPhase(PHASE.MENU)}>Menu</button>
            </div>
          </div>
        </div>
      )}

      {phase === PHASE.NO_LIVES && (
        <div className="card" style={{maxWidth:'600px', margin:'2rem auto'}}>
          <div className="no-lives-wrap">
            <div style={{fontSize:'3rem', marginBottom:'1rem'}}>💔</div>
            <h2 style={{textAlign:'center'}}>Out of Lives</h2>
            <p className="muted" style={{textAlign:'center', marginBottom:'0.5rem'}}>Lives regen 1 per hour automatically.</p>
            {nextRegen && <div className="regen-info" style={{textAlign:'center', marginBottom:'2rem'}}>Next life in: {Math.max(0, Math.ceil((nextRegen - Date.now()) / 60000))} minutes</div>}
            <p style={{textAlign:'center', marginBottom:'1.5rem', fontFamily:'Cinzel Decorative', fontSize:'0.9rem', color:'var(--gold)'}}>Or buy a pack to keep fighting</p>
            <div className="pack-grid">
              {[{id:'starter',lives:20,name:'Starter',price:'$2.99'},{id:'warrior',lives:60,name:'Warrior',price:'$6.99'},{id:'legend',lives:150,name:'Legend',price:'$14.99'}].map(p => (
                <div className="pack-card" key={p.id} onClick={() => buyPack(p.id)}>
                  <div className="pack-lives">{p.lives}</div>
                  <div className="pack-name">{p.name} Pack</div>
                  <div className="pack-price">{p.price}</div>
                </div>
              ))}
            </div>
            <div style={{textAlign:'center', marginTop:'1.5rem'}}>
              <button className="btn ghost" onClick={() => setPhase(PHASE.MENU)}>Wait for Regen</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function Ladder({ profile }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/ladder`).then(r => r.json()).then(setRows).catch(() => {});
  }, []);
  return (
    <main className="main">
      <div className="card">
        <h2>⚔ Global Ladder</h2>
        <table className="ladder-table">
          <thead><tr><th>#</th><th>Warrior</th><th>ELO</th><th>W/L</th></tr></thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.game_tag}>
                <td className="rank-num">{i===0?'👑':i===1?'🥈':i===2?'🥉':p.rank}</td>
                <td>{p.game_tag}{p.game_tag === profile?.game_tag && <span className="you-tag">YOU</span>}</td>
                <td className="elo-val">{p.elo}</td>
                <td className="wl">{p.wins}W {p.losses}L</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="mono muted" style={{textAlign:'center', padding:'2rem', fontSize:'0.8rem'}}>No warriors yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function Profile({ user, profile, setProfile }) {
    const navigate = useNavigate();
    const [history, setHistory] = useState([]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        fetch(`${API}/api/players/${user.id}/history`).then(r => r.json()).then(setHistory).catch(() => {
        });
    }, [user, navigate]);

    const buyPack = async (pack) => {
        const res = await fetch(`${API}/api/stripe/checkout`, {
            method: 'POST', headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({pack, userId: user.id})
        });
        const {url} = await res.json();
        window.location.href = url;
    };

    if (!profile) return null;

    return (
        <main className="main">
            <div className="card gold" style={{marginBottom: '1.5rem'}}>
                <h2>{profile.game_tag}</h2>
                <div className="stat-grid">
                    {[['ELO', profile.elo], ['Wins', profile.wins], ['Losses', profile.losses]].map(([k, v]) => (
                        <div className="stat-box" key={k}>
                            <div className="stat-val">{v}</div>
                            <div className="stat-key">{k}</div>
                        </div>
                    ))}
                </div>
                <div className="lives-bar">
                    {Array.from({length: 10}).map((_, i) => <div key={i}
                                                                 className={`life-pip ${i < profile.lives ? '' : 'empty'}`}/>)}
                    <div className="lives-label">{profile.lives}/10 lives · regen 1/hr</div>
                </div>
            </div>

            <div className="card" style={{marginBottom: '1.5rem'}}>
                <h2>Buy Lives</h2>
                <div className="pack-grid">
                    {[{id: 'starter', lives: 20, name: 'Starter', price: '$2.99'}, {
                        id: 'warrior',
                        lives: 60,
                        name: 'Warrior',
                        price: '$6.99'
                    }, {id: 'legend', lives: 150, name: 'Legend', price: '$14.99'}].map(p => (
                        <div className="pack-card" key={p.id} onClick={() => buyPack(p.id)}>
                            <div className="pack-lives">{p.lives}</div>
                            <div className="pack-name">{p.name} Pack</div>
                            <div className="pack-price">{p.price}</div>
                        </div>
                    ))}
                </div>
            </div>

            {history.length > 0 && (
                <div className="card">
                    <h2>Recent Battles</h2>
                    {history.map(b => (
                        <div key={b.id} style={{
                            padding: '0.75rem 0',
                            borderBottom: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div className="mono" style={{fontSize: '0.8rem'}}>
                                {b.p1?.game_tag} <span className="muted">vs</span> {b.p2?.game_tag}
                            </div>
                            <div className="mono" style={{
                                fontSize: '0.75rem',
                                color: b.winner_id === user.id ? '#4caf50' : 'var(--crimson2)'
                            }}>
                                {b.winner_id === user.id ? 'WIN' : 'LOSS'}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </main>
    );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-links">
        <a className="footer-link" href="mailto:angeloguarino.a@gmail.com">angeloguarino.a@gmail.com</a>
        <span className="footer-dot">·</span>
        <a className="footer-link" href="https://github.com/nglguarino" target="_blank" rel="noreferrer">GitHub</a>
        <span className="footer-dot">·</span>
        <a className="footer-link" href="#">Privacy Policy</a>
        <span className="footer-dot">·</span>
        <a className="footer-link" href="#">Terms of Service</a>
      </div>
      <div className="footer-copy">© 2026 Angelo Guarino · Prompt Wars</div>
    </footer>
  );
}